'use client';

import { IconButton, Menu, Text, toasterApi } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { Portal } from '@ark-ui/react';
import {
  ArrowRight01Icon,
  Download01Icon,
  LinkSquare02Icon,
  ImportantBookIcon,
  MoreVerticalCircle01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  CoPilotSessionExportMenuItems,
  resolveCoPilotExportErrorMessage,
  useCoPilotExportChatTitle,
  useCoPilotSessionExport,
  type CoPilotSessionExportFormat,
} from './session-export';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { resolveCoPilotTraceExecutionState } from '../../utils/resolve-co-pilot-trace-status';

const EMPTY_EXPORT_MESSAGES: TerraUIMessage[] = [];

type CoPilotChatOverflowMenuProps = {
  targetId?: string;
  threadId?: string;
  showPopOut?: boolean;
  showBringBack?: boolean;
  onPopOut?: () => void;
  onBringBack?: () => void;
  disabled?: boolean;
};

export function CoPilotChatOverflowMenu({
  targetId,
  threadId,
  showPopOut = false,
  showBringBack = false,
  onPopOut,
  onBringBack,
  disabled = false,
}: CoPilotChatOverflowMenuProps) {
  const t = useTranslations('chat.copilot.toolbar');
  const tDetached = useTranslations('proxy.detachedWindows');
  const exportChatTitle = useCoPilotExportChatTitle(threadId);
  const chatInstance = useChatInstancesStore((state) =>
    threadId ? state.chats.get(threadId) : undefined,
  );
  const exportMessages = chatInstance?.messages ?? EMPTY_EXPORT_MESSAGES;
  const exportStatus = useMemo(() => {
    return resolveCoPilotTraceExecutionState({
      chatStatus: chatInstance?.status ?? null,
      history: exportMessages,
    }).traceStatus;
  }, [chatInstance?.status, exportMessages]);
  const { exportSession, exportingFormat, isExporting, canExport } =
    useCoPilotSessionExport({
      threadId,
      applicationId: targetId,
      messages: exportMessages,
      status: exportStatus,
      chatTitle: exportChatTitle,
    });
  const handleExport = useCallback(
    async (format: CoPilotSessionExportFormat) => {
      try {
        await exportSession(format);
      } catch (error: unknown) {
        toasterApi.error({
          title: resolveCoPilotExportErrorMessage({
            error,
            fallback: t('exportError'),
            emptyMessage: t('exportEmpty'),
          }),
        });
      }
    },
    [exportSession, t],
  );
  const isDisabled = disabled || isExporting;
  const canExportFromHook = canExport;

  return (
    <Menu.Root
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-end',
        offset: { mainAxis: 8, crossAxis: 0 },
      }}
    >
      <Menu.Trigger asChild>
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={t('moreActions')}
          disabled={isDisabled}
          flexShrink={0}
          color="fg.muted"
          _hover={{
            color: 'fg.default',
            bg: 'transparent',
          }}
          w="7"
          h="7"
        >
          <MoreVerticalCircle01Icon size={16} />
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW="160px" py="1">
            {canExportFromHook && !isExporting ? (
              <Menu.Root
                positioning={{
                  strategy: 'fixed',
                  placement: 'right-start',
                }}
              >
                <Menu.TriggerItem fontSize="xs" py="1" px="2">
                  <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                    <Download01Icon size={12} />
                    <Text fontSize="xs">{t('exportTrace')}</Text>
                  </Box>
                  <Menu.Indicator>
                    <ArrowRight01Icon size={12} />
                  </Menu.Indicator>
                </Menu.TriggerItem>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content minW="160px" py="1">
                      <CoPilotSessionExportMenuItems
                        disabled={isExporting}
                        exportingFormat={exportingFormat}
                        onExport={(format) => {
                          void handleExport(format);
                        }}
                      />
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            ) : (
              <Menu.Item
                value="export-trace"
                fontSize="xs"
                py="1"
                px="2"
                disabled
              >
                <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                  <Download01Icon size={12} />
                  <Text fontSize="xs">
                    {isExporting ? t('exporting') : t('exportTrace')}
                  </Text>
                </Box>
              </Menu.Item>
            )}
            {showPopOut ? (
              <Menu.Item
                value="pop-out"
                fontSize="xs"
                py="1"
                px="2"
                onClick={() => {
                  onPopOut?.();
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <LinkSquare02Icon size={12} />
                  <Text fontSize="xs">{tDetached('popOutCopilot')}</Text>
                </Box>
              </Menu.Item>
            ) : null}
            {showBringBack ? (
              <Menu.Item
                value="bring-back"
                fontSize="xs"
                py="1"
                px="2"
                onClick={() => {
                  void onBringBack?.();
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <ImportantBookIcon size={12} />
                  <Text fontSize="xs">{tDetached('reattachCopilot')}</Text>
                </Box>
              </Menu.Item>
            ) : null}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
